import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import LookupSelect from '../components/LookupSelect';
import { lookupProvider } from '../lookups';

/**
 * Add a facility.
 *
 * **Its own page, unlike Area and Provider**, which fold create into their detail
 * page. Those two forms are the same fields either way; this one is not. The old
 * `FacilityAdd` asks nine questions, while the facility detail page has thirty
 * fields, four cropped images and a locker picker — a facility is *created*
 * thin and *filled in* afterwards. Folding them together would mean one form
 * that hides two thirds of itself, which is a worse lie than two files.
 *
 * The payload is `saveNewFacilityData`'s, exactly as the old form built it,
 * including the part nobody would guess: **eight hardcoded image keys**. A
 * facility created without them has no logo and no photos at any size, and the
 * public site renders a broken card — so they travel as the old form's literal
 * defaults, and the real pictures are set on the detail page afterwards.
 *
 * `Provider` and `Area` go as `{ value, label }` because the api module reads
 * `.value` off each.
 *
 * The validation is the old page's six mandatory fields, moved from a message
 * after the save attempt to a disabled button that says which field is missing.
 */

// The old create form's literal defaults. Not decoration: `/admin/facility/create`
// stores whatever it is given, so a facility created with empty picture keys is a
// facility with no pictures anywhere it appears.
const PICTURE_DEFAULTS = {
  PlaceLogoUrl: '7c2f125f-b311-4ac1-b698-1ef6b753455b-squared',
  PlacePhotoUrl: '1ee9d569-4a6d-4e2e-b325-300b946f9923-list',
  PlacePhotoListUrl: '1ee9d569-4a6d-4e2e-b325-300b946f9923-list',
  PlacePhotoListPreviewUrl: '62def2d9-faa8-407e-ac20-30a06fba817a-list-preview',
  PlacePhotoSquareUrl: '7c2f125f-b311-4ac1-b698-1ef6b753455b-squared',
  PlacePhotoSquarePreviewUrl: '40795af1-8f6f-4d65-842d-e6329b24d5f8-squared-preview',
  PlacePhotoHugeUrl: 'ab7687a7-6ad0-48a9-a2c0-6c68850a5691-huge',
  PlacePhotoHugePreviewUrl: 'bbf96340-d71f-488b-86be-ff2394c901b2-huge-preview'
};

// A 1×1 transparent PNG, as the old form sent it.
const PICTURE_PREVIEW = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAADCAYAAABbNsX4AAAACXBIWXMAAA7EAAAOxAGVKw4bAAAASUlEQVR4nGORMA36rywnxXD19kMGfj5uBmEBPgYWBWkJBlUFGYYQTzuGF6/fMRw9e5WB5ezV2wz/GRgY9h0/z8DLw8XAwszEAAAl8BGj0uhjlQAAAABJRU5ErkJggg==';

const EMPTY = {
  Name: '',
  PlaceAddressText: '',
  PlaceLatitude: '',
  PlaceLongitude: '',
  PlaceEmailAddressInner: '',
  PlaceWebsiteUrl: '',
  PlacePhoneNumber: '',
  Slug: ''
};

const number = value => value === '' || Number.isFinite(Number(value));

const FacilityCreateInternal = ({ api }) => {
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [provider, setProvider] = useState(null);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const set = (name, value) => setForm(previous => ({ ...previous, [name]: value }));

  const errors = {
    Name: form.Name.trim() ? null : 'Name is required.',
    PlaceAddressText: form.PlaceAddressText.trim() ? null : 'Address is required.',
    PlaceLatitude: !String(form.PlaceLatitude).trim()
      ? 'Latitude is required.'
      : (number(form.PlaceLatitude) ? null : 'Not a number.'),
    PlaceLongitude: !String(form.PlaceLongitude).trim()
      ? 'Longitude is required.'
      : (number(form.PlaceLongitude) ? null : 'Not a number.')
  };
  const missing = [
    ...Object.values(errors).filter(Boolean),
    provider ? null : 'A provider is required.',
    area ? null : 'An area is required.'
  ].filter(Boolean);

  const save = async event => {
    event.preventDefault();
    if (missing.length || busy) return;

    setBusy(true);
    setNote(null);
    try {
      const created = await api.facilities.saveNewFacilityData({
        ...form,
        // Read as `.value` by the api module.
        Provider: provider,
        Area: area,
        // The rest of the old form's defaults, unchanged.
        Summary: '',
        ContactName: '',
        ContactNumber: '',
        ContactEmail: '',
        Type: 'Undefined',
        Category: 'None',
        PlaceAddress: '',
        PlaceMapsUrl: '',
        PlaceName: '',
        PlaceRating: '',
        PlaceId: '',
        PlaceOpeningHours: [],
        PicturePreview: PICTURE_PREVIEW,
        ...PICTURE_DEFAULTS
      });

      if (!created || !created.Id) {
        setNote({ tone: 'error', text: 'The facility was not created — the server answered without an id.' });
        return;
      }
      navigate(`/next/facility/${created.Id}`);
    } catch (error) {
      setNote({
        tone: 'error',
        text: error?.response?.data?.ErrorMessage || error?.message || 'Unable to create the facility.'
      });
    } finally {
      setBusy(false);
    }
  };

  const field = (name, label, { hint, placeholder } = {}) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      <input
        className="next-input"
        value={form[name]}
        placeholder={placeholder}
        onChange={event => set(name, event.target.value)}
      />
      {(errors[name] || hint) && (
        <span className={errors[name] ? 'next-form__error' : 'next-form__hint'}>
          {errors[name] || hint}
        </span>
      )}
    </label>
  );

  return (
    <div className="next-page">
      <BackToList to="/next/facilities">← All facilities</BackToList>

      <header className="next-page__header">
        <h1>New facility</h1>
        <p className="next-muted">
          The few things a facility cannot exist without. Everything else — description,
          contact, pictures, opening hours, locker — is on its page once it is created.
        </p>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <form onSubmit={save}>
        <section className="next-card">
          <div className="next-card__head"><h2>Venue</h2></div>
          <div className="next-form__grid">
            {field('Name', 'Name')}
            {field('Slug', 'Slug', { hint: 'Its part of the public URL. Left empty, the backend picks one.' })}
            {field('PlaceAddressText', 'Address')}
            {field('PlaceLatitude', 'Latitude', { placeholder: '56.1629' })}
            {field('PlaceLongitude', 'Longitude', { placeholder: '10.2039' })}
          </div>
        </section>

        <section className="next-card">
          <div className="next-card__head"><h2>Ownership</h2></div>
          <div className="next-form__grid">
            <LookupSelect
              label="Provider"
              value={provider?.value || ''}
              placeholder="Type a provider name or paste an id"
              loadOptions={key => lookupProvider(api, key)}
              onChange={value => setProvider(value ? { value, label: '' } : null)}
            />
            <LookupSelect
              label="Area"
              value={area?.value || ''}
              placeholder="Type an area name or paste an id"
              loadOptions={key => api.areas.lookupByNameOrId(key)}
              onChange={value => setArea(value ? { value, label: '' } : null)}
            />
          </div>
        </section>

        <section className="next-card">
          <div className="next-card__head">
            <h2>Contact</h2>
            <span className="next-muted">optional</span>
          </div>
          <div className="next-form__grid">
            {field('PlaceEmailAddressInner', 'Email')}
            {field('PlacePhoneNumber', 'Phone number')}
            {field('PlaceWebsiteUrl', 'Website')}
          </div>

          <div className="next-form__actions">
            <button
              type="submit"
              className="next-btn next-btn--primary"
              disabled={!!missing.length || busy}
            >
              {busy ? 'Creating…' : 'Create facility'}
            </button>
            <Link className="next-btn" to="/next/facilities">Cancel</Link>
            {!!missing.length && (
              <span className="next-form__hint">{missing.join(' ')}</span>
            )}
          </div>

          <p className="next-form__hint">
            Created with WannaSport’s placeholder pictures. Replace them on the facility’s
            own page — until then it shows the same logo and photos as every other new venue.
          </p>
        </section>
      </form>
    </div>
  );
};

FacilityCreateInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const FacilityCreate = withApi(FacilityCreateInternal);

FacilityCreate.requiresLogin = true;

export default FacilityCreate;
